import { isAndroid, isIos, isIpad } from './install'

/**
 * A name for this device, guessed so the skipper does not have to invent one.
 *
 * The old app recorded whatever the OS called the handset -- `SM-A320FL` and the like. A
 * browser will not say that much unless asked: Chromium's user-agent client hints hand over
 * the model on request, which is where "Pixel 8" comes from. Everywhere else the make of the
 * thing is as far as it goes, and "iPhone" is a perfectly good name for the only iPhone the
 * reader is holding.
 *
 * It is a suggestion, in a field the reader can overwrite; nothing turns on it being right.
 */

interface UADataLike {
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platform?: string }>
}

function byUserAgent(): string {
  if (isIpad()) return 'iPad'
  if (isIos()) return 'iPhone'
  if (isAndroid()) return 'Android'
  const ua = navigator.userAgent
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows-pc'
  if (/CrOS/.test(ua)) return 'Chromebook'
  if (/Linux/.test(ua)) return 'Linux-pc'
  return ''
}

/** The model where the browser will say it, the make of the device otherwise, '' when neither */
export async function guessDeviceName(): Promise<string> {
  const data = (navigator as Navigator & { userAgentData?: UADataLike }).userAgentData
  try {
    const high = await data?.getHighEntropyValues?.(['model'])
    const model = high?.model?.trim()
    // desktop Chromium answers with an empty model, which is not a name for anything
    if (model) return model.slice(0, 60)
  } catch {
    // the browser declined, or does not know what a client hint is
  }
  return byUserAgent()
}
